import React from 'react';
import Icon from '../../../components/AppIcon';

const RateLimits = () => {
  const tiers = [
    {
      name: 'Developer',
      price: 'Free',
      requests: '1,000',
      period: 'per hour',
      features: [
        'Basic API access',
        'Standard rate limits',
        'Community support',
        'Public documentation'
      ],
      color: 'gray'
    },
    {
      name: 'Professional',
      price: '$299/mo',
      requests: '10,000',
      period: 'per hour',
      features: [
        'Increased rate limits',
        'Webhook support',
        'Email support',
        'Advanced analytics'
      ],
      color: 'blue',
      popular: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      requests: 'Unlimited',
      period: 'custom limits',
      features: [
        'Unlimited requests',
        'Dedicated support',
        'SLA guarantees',
        'Custom integrations'
      ],
      color: 'purple'
    }
  ];

  const limits = [
    {
      endpoint: 'GET /v2/policies',
      developer: '100/min',
      professional: '1,000/min',
      enterprise: 'Unlimited'
    },
    {
      endpoint: 'GET /v2/policies/{id}',
      developer: '200/min',
      professional: '2,000/min',
      enterprise: 'Unlimited'
    },
    {
      endpoint: 'POST /v2/search',
      developer: '50/min',
      professional: '500/min',
      enterprise: 'Unlimited'
    },
    {
      endpoint: 'GET /v2/analytics/*',
      developer: '20/min',
      professional: '200/min',
      enterprise: 'Unlimited'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      gray: 'bg-gray-100 text-gray-700 border-gray-300',
      blue: 'bg-blue-100 text-blue-700 border-blue-300',
      purple: 'bg-purple-100 text-purple-700 border-purple-300'
    };
    return colors?.[color] || colors?.gray;
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Rate Limits & Pricing Tiers
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Flexible rate limits designed to scale with your needs. From development to enterprise production workloads.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-12">
          {tiers?.map((tier) => (
            <div
              key={tier?.name}
              className={`relative bg-white rounded-lg shadow-lg border-2 p-6 md:p-8 ${
                tier?.popular ? 'border-blue-600' : 'border-gray-200'
              }`}
            >
              {tier?.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="px-4 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                  {tier?.name}
                </h3>
                <div className="mb-4">
                  <span className="text-3xl md:text-4xl font-bold text-gray-900">
                    {tier?.price}
                  </span>
                </div>
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${getColorClasses(tier?.color)}`}>
                  <span className="text-xl md:text-2xl font-bold">{tier?.requests}</span>
                  <span className="text-sm">{tier?.period}</span>
                </div>
              </div>

              <ul className="space-y-3 mb-6">
                {tier?.features?.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                    <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 md:px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg md:text-xl font-semibold text-gray-900">
              Endpoint-Specific Rate Limits
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Endpoint
                  </th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Developer
                  </th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Professional
                  </th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Enterprise
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {limits?.map((limit, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 md:px-6 py-4">
                      <code className="text-xs md:text-sm text-blue-600 font-mono">
                        {limit?.endpoint}
                      </code>
                    </td>
                    <td className="px-4 md:px-6 py-4 text-sm text-gray-700">
                      {limit?.developer}
                    </td>
                    <td className="px-4 md:px-6 py-4 text-sm text-gray-700">
                      {limit?.professional}
                    </td>
                    <td className="px-4 md:px-6 py-4 text-sm text-gray-700">
                      {limit?.enterprise}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} color="var(--color-secondary)" className="flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-base font-semibold text-gray-900 mb-2">Rate Limit Headers</h4>
                <p className="text-sm text-gray-700 mb-3">
                  Every API response includes rate limit information in headers:
                </p>
                <code className="text-xs bg-white px-2 py-1 rounded block mb-1">X-RateLimit-Limit: 1000</code>
                <code className="text-xs bg-white px-2 py-1 rounded block mb-1">X-RateLimit-Remaining: 847</code>
                <code className="text-xs bg-white px-2 py-1 rounded block">X-RateLimit-Reset: 1706422951</code>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-base font-semibold text-gray-900 mb-2">Rate Limit Exceeded</h4>
                <p className="text-sm text-gray-700 mb-3">
                  When limits are exceeded, you'll receive a 429 status code:
                </p>
                <div className="bg-gray-900 rounded p-3">
                  <pre className="text-green-400 text-xs">
                    <code>{`{
  "error": "rate_limit_exceeded",
  "message": "Too many requests",
  "retry_after": 60
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RateLimits;